import { useState } from 'react';
import {
  ClipboardTextIcon,
  PlusIcon,
  SpeedometerIcon,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { AccentButtonOutline } from '../../../../../../../shared/Buttons';
import { Asset } from '../../../../../../../types/apiTypes';
import { useDemo } from '../../../../../../../utils/DemoContext';
import { ClipLoader } from 'react-spinners';
import { useScreenView } from '../../../../../../../utils/ScreenViewContext';
import { Trailer, Truck } from '../../../../../../../types/dbTypes';
import PMChecklistModal from '../../../resources/PMChecklistModal';
import FleetInsuranceControl from '../fleet-insurance/FleetInsuranceControl';
import { capitalize } from '../../../../../../../utils/capitalize';

export type CarrierAsset = {
  asset_type: string;
  sub_type: string;
  vin_number: string;
  make: string;
  model: string;
  year: string;
  ownership_status: 'Lease to Own' | 'Owned' | 'Leased';
  pay_for_pms_repairs: boolean;
  tire_quality: 'premium' | 'intermediate' | 'basic';
};

function AssetList({
  addClass,
  carrierAssetsData,
  setSelectedAsset,
  loadingAssets,
  onAssetChange,
  isPreActive,
}: {
  addClass?: string;
  carrierAssetsData: Asset[];
  setSelectedAsset: (asset: Asset) => void;
  loadingAssets: boolean;
  onAssetChange: () => void;
  isPreActive: boolean;
}) {
  const { screenView } = useScreenView();
  const navigate = useNavigate();
  const { isDemo } = useDemo();
  const [isPmChecklistOpen, setIsPmChecklistOpen] = useState(false);

  const renderAssetDisplayName = (asset: Asset) => {
    if (asset?.asset_type === 'truck') {
      const unitNumberTruck = (asset as Truck)?.truck_display_name;
      return unitNumberTruck;
    } else {
      const unitNumberTrailer = (asset as Trailer)?.trailer_display_name;
      return unitNumberTrailer;
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Assets
              </h2>
              <div className="flex flex-row items-center justify-between space-x-2">
                <AccentButtonOutline
                  onClick={() => navigate('/dashboard/assets/create')}
                  disabled={isDemo}
                  addClass="rounded-full text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <PlusIcon size={20} />
                  <h2 className="text-[14px]">Add Asset</h2>
                </AccentButtonOutline>
                <AccentButtonOutline
                  onClick={() => navigate('/dashboard/assets/submit-mileage')}
                  disabled={isPreActive}
                  addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <SpeedometerIcon size={20} />
                  <h2 className="text-[14px]">Submit Mileage</h2>
                </AccentButtonOutline>
                <AccentButtonOutline
                  onClick={() => setIsPmChecklistOpen(true)}
                  addClass="rounded-full text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
                >
                  <ClipboardTextIcon size={20} />
                  <h2 className="text-[14px]">PM Checklist</h2>
                </AccentButtonOutline>
              </div>
            </div>
            {/* THT-675 — fleet-wide insurance premium lives with the assets it
                governs, directly under this widget's header. */}
            <FleetInsuranceControl
              assets={carrierAssetsData}
              loadingAssets={loadingAssets}
              disabled={isDemo}
            />
            {!loadingAssets ? (
              <div className="w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white shadow-md">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Type
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Sub Type
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Unit Number
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        VIN Number
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Make
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Model
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Year
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Ownership Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {carrierAssetsData.map((asset: any, index) =>
                      asset?.asset_type === 'truck' ? (
                        <tr
                          onClick={
                            isDemo
                              ? undefined
                              : () => {
                                  setSelectedAsset(asset);
                                  navigate(
                                    `/dashboard/assets/${asset?.asset_type}/${asset?.asset_type === 'truck' ? asset?.truck_id : asset?.trailer_id}/overview`
                                  );
                                }
                          }
                          key={index}
                          className={`border-b border-gray-200 ${isDemo ? '' : 'hover:cursor-pointer hover:bg-ctaactivegreen'}`}
                        >
                          <td className="px-4 py-2 text-[14px] text-primary">
                            <img
                              alt="truck-svg"
                              src="/truck.svg"
                              className="h-[24px] w-[24px] text-primary"
                            />
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {`Class ${asset?.truck_class_number} (${asset?.truck_class})`}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {renderAssetDisplayName(asset)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.vehicle_id_number}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.truck_make)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.truck_model)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.truck_year}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.ownership_status === 'leaseToOwn'
                              ? 'Lease to Own'
                              : capitalize(asset?.ownership_status)}
                          </td>
                        </tr>
                      ) : (
                        <tr
                          onClick={
                            isDemo
                              ? undefined
                              : () => {
                                  setSelectedAsset(asset);
                                  navigate(
                                    `/dashboard/assets/${asset?.asset_type}/${asset?.asset_type === 'truck' ? asset?.truck_id : asset?.trailer_id}/overview`
                                  );
                                }
                          }
                          key={index}
                          className={`border-b border-gray-200 ${isDemo ? '' : 'hover:cursor-pointer hover:bg-ctaactivegreen'}`}
                        >
                          <td className="px-4 py-2 text-[14px] text-primary">
                            <img
                              alt="trailer-svg"
                              src="/trailer.svg"
                              className="h-[24px] w-[24px] text-primary"
                            />
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            Trailer
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {renderAssetDisplayName(asset)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.vehicle_id_number}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.trailer_make)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.trailer_model)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.trailer_year}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.ownership_status === 'leaseToOwn'
                              ? 'Lease to Own'
                              : capitalize(asset?.ownership_status)}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingAssets}
                size={40}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full justify-center"
              />
            )}
          </div>
        );
      case 'tablet':
        return (
          <div
            className={`flex w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Assets
              </h2>
              <AccentButtonOutline
                onClick={() => navigate('/dashboard/assets/create')}
                addClass="rounded-full text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
              >
                <PlusIcon size={20} />
                <h2 className="text-[14px]">Add Asset</h2>
              </AccentButtonOutline>
              <AccentButtonOutline
                onClick={() => navigate('/dashboard/assets/submit-mileage')}
                disabled={isPreActive}
                addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
              >
                <SpeedometerIcon size={20} />
                <h2 className="text-[14px]">Submit Mileage</h2>
              </AccentButtonOutline>
              <AccentButtonOutline
                onClick={() => setIsPmChecklistOpen(true)}
                addClass="rounded-full text-primary h-[32px] w-[196px] max-w-[196px] flex flex-row items-center justify-center space-x-2 align-middle"
              >
                <ClipboardTextIcon size={20} />
                <h2 className="text-[14px]">PM Checklist</h2>
              </AccentButtonOutline>
            </div>
            {/* THT-675 — fleet-wide insurance premium lives with the assets it
                governs, directly under this widget's header. */}
            <FleetInsuranceControl
              assets={carrierAssetsData}
              loadingAssets={loadingAssets}
              disabled={isDemo}
            />
            {!loadingAssets ? (
              <div className="w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white shadow-md">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Type
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Sub Type
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Unit Number
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        VIN Number
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Make
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Model
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Year
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Ownership Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {carrierAssetsData.map((asset: any, index) =>
                      asset?.asset_type === 'truck' ? (
                        <tr
                          onClick={
                            isDemo
                              ? undefined
                              : () => {
                                  setSelectedAsset(asset);
                                  navigate(
                                    `/dashboard/assets/${asset?.asset_type}/${asset?.asset_type === 'truck' ? asset?.truck_id : asset?.trailer_id}/overview`
                                  );
                                }
                          }
                          key={index}
                          className={`border-b border-gray-200 ${isDemo ? '' : 'hover:cursor-pointer hover:bg-ctaactivegreen'}`}
                        >
                          <td className="px-4 py-2 text-[14px] text-primary">
                            <img
                              alt="truck-svg"
                              src="/truck.svg"
                              className="h-[24px] w-[24px] text-primary"
                            />
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            Truck Class ({asset?.truck_class})
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {renderAssetDisplayName(asset)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.vehicle_id_number}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.truck_make)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.truck_model)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.truck_year}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.ownership_status === 'leaseToOwn'
                              ? 'Lease to Own'
                              : capitalize(asset?.ownership_status)}
                          </td>
                        </tr>
                      ) : (
                        <tr
                          onClick={
                            isDemo
                              ? undefined
                              : () => {
                                  setSelectedAsset(asset);
                                  navigate(
                                    `/dashboard/assets/${asset?.asset_type}/${asset?.asset_type === 'truck' ? asset?.truck_id : asset?.trailer_id}/overview`
                                  );
                                }
                          }
                          key={index}
                          className={`border-b border-gray-200 ${isDemo ? '' : 'hover:cursor-pointer hover:bg-ctaactivegreen'}`}
                        >
                          <td className="px-4 py-2 text-[14px] text-primary">
                            <img
                              alt="trailer-svg"
                              src="/trailer.svg"
                              className="h-[24px] w-[24px] text-primary"
                            />
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            Trailer
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {renderAssetDisplayName(asset)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.vehicle_id_number}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.trailer_make)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.trailer_model)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.trailer_year}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.ownership_status === 'leaseToOwn'
                              ? 'Lease to Own'
                              : capitalize(asset?.ownership_status)}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingAssets}
                size={40}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full justify-center"
              />
            )}
          </div>
        );
      case 'mobile':
        return (
          <div
            className={`flex w-full flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass}`}
          >
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Assets
              </h2>
              <AccentButtonOutline
                onClick={() => navigate('/dashboard/assets/create')}
                disabled={isDemo}
                addClass="rounded-full text-primary h-[32px] w-[170] max-w-[170] flex flex-row items-center justify-center space-x-2 align-middle disabled:cursor-not-allowed disabled:opacity-60"
              >
                <PlusIcon size={16} />
                <h2 className="text-[11px]">Add Asset</h2>
              </AccentButtonOutline>
              <AccentButtonOutline
                onClick={() => navigate('/dashboard/assets/submit-mileage')}
                disabled={isPreActive}
                addClass="rounded-full disabled:cursor-not-allowed disabled:opacity-60 text-primary h-[32px] w-[170] max-w-[170] flex flex-row items-center justify-center space-x-2 align-middle"
              >
                <SpeedometerIcon size={20} />
                <h2 className="text-[11px]">Submit Mileage</h2>
              </AccentButtonOutline>
              <AccentButtonOutline
                onClick={() => setIsPmChecklistOpen(true)}
                addClass="rounded-full text-primary h-[32px] w-[170] max-w-[170] flex flex-row items-center justify-center space-x-2 align-middle"
              >
                <ClipboardTextIcon size={16} />
                <h2 className="text-[11px]">PM Checklist</h2>
              </AccentButtonOutline>
            </div>
            {/* THT-675 — fleet-wide insurance premium lives with the assets it
                governs, directly under this widget's header. */}
            <FleetInsuranceControl
              assets={carrierAssetsData}
              loadingAssets={loadingAssets}
              disabled={isDemo}
            />
            {!loadingAssets ? (
              <div className="w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white shadow-md">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                        Type
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Sub Type
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Unit Number
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        VIN Number
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Make
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Model
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Year
                      </th>
                      <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                        Ownership Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {carrierAssetsData.map((asset: any, index) =>
                      asset?.asset_type === 'truck' ? (
                        <tr
                          onClick={
                            isDemo
                              ? undefined
                              : () => {
                                  setSelectedAsset(asset);
                                  navigate(
                                    `/dashboard/assets/${asset?.asset_type}/${asset?.asset_type === 'truck' ? asset?.truck_id : asset?.trailer_id}/overview`
                                  );
                                }
                          }
                          key={index}
                          className={`border-b border-gray-200 ${isDemo ? '' : 'hover:cursor-pointer hover:bg-ctaactivegreen'}`}
                        >
                          <td className="px-4 py-2 text-[14px] text-primary">
                            <img
                              alt="truck-svg"
                              src="/truck.svg"
                              className="h-[24px] w-[24px] text-primary"
                            />
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            Truck Class ({asset?.truck_class})
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {renderAssetDisplayName(asset)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.vehicle_id_number}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.truck_make)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.truck_model)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.truck_year}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.ownership_status === 'leaseToOwn'
                              ? 'Lease to Own'
                              : capitalize(asset?.ownership_status)}
                          </td>
                        </tr>
                      ) : (
                        <tr
                          onClick={
                            isDemo
                              ? undefined
                              : () => {
                                  setSelectedAsset(asset);
                                  navigate(
                                    `/dashboard/assets/${asset?.asset_type}/${asset?.asset_type === 'truck' ? asset?.truck_id : asset?.trailer_id}/overview`
                                  );
                                }
                          }
                          key={index}
                          className={`border-b border-gray-200 ${isDemo ? '' : 'hover:cursor-pointer hover:bg-ctaactivegreen'}`}
                        >
                          <td className="px-4 py-2 text-[14px] text-primary">
                            <img
                              alt="trailer-svg"
                              src="/trailer.svg"
                              className="h-[24px] w-[24px] text-primary"
                            />
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            Trailer
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {renderAssetDisplayName(asset)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.vehicle_id_number}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.trailer_make)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {capitalize(asset?.trailer_model)}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.trailer_year}
                          </td>
                          <td className="px-4 py-2 text-right text-[14px] text-primary">
                            {asset?.ownership_status === 'leaseToOwn'
                              ? 'Lease to Own'
                              : capitalize(asset?.ownership_status)}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingAssets}
                size={40}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full justify-center"
              />
            )}
          </div>
        );
    }
  };
  return (
    <>
      {renderScreenView(screenView)}
      <PMChecklistModal
        isOpen={isPmChecklistOpen}
        onClose={() => setIsPmChecklistOpen(false)}
      />
    </>
  );
}

export default AssetList;
